export interface AutocompleteService {
  getPlacePredictions(
    request: AutocompletionRequest,
    callback: (
      result: AutocompletePrediction[],
      status: PlacesServiceStatus,
    ) => void,
  ): void;
  getQueryPredictions(
    request: QueryAutocompletionRequest,
    callback: (
      result: QueryAutocompletePrediction[],
      status: PlacesServiceStatus,
    ) => void,
  ): void;
}

export interface QueryAutocompletePrediction {
  description: string;
  matched_substrings: PredictionSubstring[];
  place_id: string;
  terms: PredictionTerm[];
}

export interface QueryAutocompletionRequest {
  bounds?: LatLngBounds | LatLngBoundsLiteral;
  input?: string;
  location?: LatLng;
  offset?: number;
  radius?: number;
}

// https://developers.google.com/maps/documentation/geocoding/intro#StatusCodes
export const enum PlacesServiceStatus {
  OK = 'OK',
  INVALID_REQUEST = 'INVALID_REQUEST',
  OVER_QUERY_LIMIT = 'OVER_QUERY_LIMIT',
  REQUEST_DENIED = 'REQUEST_DENIED',
  OVER_DAILY_LIMIT = 'OVER_DAILY_LIMIT',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
  ZERO_RESULTS = 'ZERO_RESULTS',
}

export interface ComponentRestrictions {
  country: string | string[];
}

export interface AutocompleteStructuredFormatting {
  main_text: string;
  main_text_matched_substrings: PredictionSubstring[];
  secondary_text: string;
}

export interface PredictionSubstring {
  length: number;
  offset: number;
}

export interface PredictionTerm {
  offset: number;
  value: string;
}

export interface AutocompletePrediction {
  description: string;
  matched_substrings: PredictionSubstring[];
  place_id: string;
  reference: string;
  structured_formatting: AutocompleteStructuredFormatting;
  terms: PredictionTerm[];
  types: string[];
}

export interface AutocompletionRequest {
  bounds?: LatLngBounds | LatLngBoundsLiteral;
  componentRestrictions?: ComponentRestrictions;
  input: string;
  location?: LatLng;
  offset?: number;
  radius?: number;
  sessionToken?: string;
  types: PredictionType;
}

interface LatLngLiteral {
  lat: number;
  lng: number;
}

export class LatLng {
  equals: (other: LatLng) => boolean;
  lat: () => number;
  lng: () => number;
  toString: () => string;
  toUrlValue: (precision?: number) => string;
  toJSON: () => LatLngLiteral;
}

interface LatLngBoundsLiteral {
  east: number;
  north: number;
  south: number;
  west: number;
}

interface LatLngBounds {
  contains: (latLng: LatLng | LatLngLiteral) => boolean;
  equals: (other: LatLngBounds | LatLngBoundsLiteral) => boolean;
  extend: (point: LatLng | LatLngLiteral) => LatLngBounds;
  getCenter: () => LatLng;
  getNorthEast: () => LatLng;
  getSouthWest: () => LatLng;
  intersects: (other: LatLngBounds | LatLngBoundsLiteral) => boolean;
  isEmpty: () => boolean;

  toJSON: () => LatLngBoundsLiteral;
  toSpan: () => LatLng;
  toString: () => string;

  toUrlValue: (precision?: number) => string;

  union: (other: LatLngBounds | LatLngBoundsLiteral) => LatLngBounds;
}

type Constructable<T> = new () => T;

export interface Sdk {
  places: {
    AutocompleteService: Constructable<AutocompleteService>;
  };
}

export type PredictionType =
  | 'geocode'
  | 'address'
  | '(cities)'
  | '(regions)'
  | 'establishment';
